import { useEffect, useRef } from "react";
import { Room, Player } from "@/hooks/useRoom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Trophy, Share2, Home } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";

interface Props {
  room: Room;
  players: Player[];
}

export default function GameOver({ room, players }: Props) {
  const navigate = useNavigate();
  const fired = useRef(false);
  const sorted = [...players].sort((a, b) => b.score - a.score);
  const winner = sorted[0];

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const end = Date.now() + 1500;
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 } });
      confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 } });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, []);

  const share = () => {
    const summary = `🧠 SameBrain results\nRoom ${room.code}\n\n${sorted
      .map((p, i) => `${i + 1}. ${p.name} — ${p.score}`)
      .join("\n")}`;
    navigator.clipboard.writeText(summary);
    toast.success("Results copied!");
  };

  const playAgain = async () => {
    // Reset scores and back to lobby
    await Promise.all(players.map((p) => supabase.from("players").update({ score: 0 }).eq("id", p.id)));
    await supabase.from("rooms").update({ status: "lobby", current_round_id: null }).eq("id", room.id);
  };

  return (
    <main className="min-h-screen p-4 py-8 flex items-center">
      <div className="max-w-md mx-auto w-full space-y-6 animate-pop-in">
        <div className="text-center">
          <Trophy className="h-16 w-16 mx-auto text-warning mb-2" />
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Game Over</div>
          <h1 className="text-4xl font-extrabold">{winner?.name} wins! 🎉</h1>
        </div>

        <Card className="p-5 border-2 shadow-pop">
          {sorted.map((p, i) => (
            <div
              key={p.id}
              className={`flex items-center gap-3 py-2 ${
                i < sorted.length - 1 ? "border-b border-border" : ""
              }`}
            >
              <div className="text-2xl font-extrabold w-8 text-center">
                {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
              </div>
              <div className={`h-3 w-3 rounded-full ${p.team === "purple" ? "bg-team-purple" : "bg-team-green"}`} />
              <div className="flex-1 font-bold">{p.name}</div>
              <div className="text-2xl font-extrabold tabular-nums">{p.score}</div>
            </div>
          ))}
        </Card>

        <div className="grid grid-cols-2 gap-2">
          <Button onClick={share} variant="outline" className="gap-2">
            <Share2 className="h-4 w-4" /> Share
          </Button>
          <Button onClick={playAgain} className="gap-2 gradient-primary font-bold">
            Play Again
          </Button>
        </div>
        <Button onClick={() => navigate("/")} variant="ghost" className="w-full gap-2">
          <Home className="h-4 w-4" /> Home
        </Button>
      </div>
    </main>
  );
}
